import React, { useState } from 'react';
import { X, Minimize2, Maximize2, Edit2 } from 'lucide-react';
import { ChatTheme } from '@/types/theme';
import { Input } from '@/components/ui/input';

/**
 * Props for ChatHeader component
 * Following OfferChat.ai TypeScript standards
 */
interface ChatHeaderProps {
  theme?: ChatTheme;
  title?: string;
  isFullscreen?: boolean;
  onClose?: () => void;
  onToggleFullscreen?: () => void;
  onTitleChange?: (newTitle: string) => void;
  companyName?: string | null;
  companyLogo?: string | null;
  className?: string;
}

/**
 * Chat widget header with customization options
 * Follows OfferChat.ai component patterns
 */
export function ChatHeader({
  theme,
  title = 'Chat',
  isFullscreen = false,
  onClose,
  onToggleFullscreen,
  onTitleChange,
  companyName,
  companyLogo,
  className = '',
}: ChatHeaderProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  return (
    <div
      className={`chat-header ${className}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
        borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
        backgroundColor: theme?.headerbackgroundcolor || theme?.primarycolor || '#497CFD',
        color: theme?.headertextcolor || '#FFFFFF',
        borderTopLeftRadius: theme?.borderradius ? `${theme.borderradius}px` : '0.75rem',
        borderTopRightRadius: theme?.borderradius ? `${theme.borderradius}px` : '0.75rem'
      }}
      role="banner"
    >
      {/* Company information (logo and/or name) */}
      <div className="company-info" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {companyLogo && (
          <div className="company-logo" style={{ height: '2rem', maxWidth: '120px' }}>
            <img
              src={companyLogo}
              alt={companyName || title}
              style={{ height: '100%', maxWidth: '100%', objectFit: 'contain' }}
            />
          </div>
        )}
        
        {/* Company name or title */}
        {isEditingTitle && onTitleChange ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (onTitleChange) onTitleChange(editedTitle);
              setIsEditingTitle(false);
            }}
            style={{ maxWidth: '150px' }}
          >
            <Input
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              autoFocus
              onBlur={() => {
                if (onTitleChange) onTitleChange(editedTitle);
                setIsEditingTitle(false);
              }}
              style={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: 'inherit',
                border: 'none',
                padding: '0.25rem 0.5rem',
                fontSize: '0.9rem'
              }}
            />
          </form>
        ) : (
          <div
            className="header-display-name"
            style={{
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
              cursor: onTitleChange ? 'pointer' : 'default'
            }}
            onClick={() => onTitleChange && setIsEditingTitle(true)}
          >
            {companyName || title}
            {onTitleChange && (
              <Edit2 size={14} style={{ opacity: 0.7 }} />
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        {onToggleFullscreen && (
          <button
            onClick={onToggleFullscreen}
            className="header-button"
            aria-label={isFullscreen ? 'Minimize chat' : 'Maximize chat'}
            title={isFullscreen ? 'Minimize' : 'Maximize'}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.25rem',
              color: 'inherit',
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
          >
            {isFullscreen ? (
              <Minimize2 size={18} />
            ) : (
              <Maximize2 size={18} />
            )}
          </button>
        )}

        {onClose && (
          <button
            onClick={onClose}
            className="header-button"
            aria-label="Close chat"
            title="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0.25rem',
              color: 'inherit',
              opacity: 0.7,
              transition: 'opacity 0.2s'
            }}
          >
            <X size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
